// frontend/src/Home.tsx

import React, { useState, useMemo } from 'react';
import useSWR from 'swr';
import { Typography, Card, Alert, Row, Col, Input, Switch, Tabs } from 'antd';
import HotCoinsBanner from './components/HotCoinsBanner';
import InvestmentPlansList from './components/InvestmentPlansList';
import RateList from './components/RateList';
import PriceHistoryChart from './components/PriceHistoryChart';
import TradingRecommendations from './components/TradingRecommendations';
import FutureForecast from './components/FutureForecast';
import type { CoinRecommendation, InvestmentPlan, Point, AnalysisData } from './types';
import { fetcher } from './utils/fetcher';

const { Title } = Typography;
const { Search } = Input;

const Home: React.FC = () => {
  const [currentSymbol, setCurrentSymbol] = useState('BTCUSDT');
  const [showDetails, setShowDetails] = useState(false);

  // exchangeInfo 使用 POST，fetcher 会自动处理
  const { data: info } = useSWR<any>('/exchangeInfo', fetcher);
  const usdtPairs = useMemo(() => {
    if (!info?.symbols) return [];
    return info.symbols
      .filter((s: any) => s.status === 'TRADING' && s.quoteAsset === 'USDT')
      .map((s: any) => s.symbol);
  }, [info]);

  const { data: popular } = useSWR<CoinRecommendation[]>('/getTopCryptocurrencies', fetcher);
  const top10 = useMemo(() => {
    if (!Array.isArray(popular)) return [];
    return popular.filter(c => usdtPairs.includes(c.symbol));
  }, [popular, usdtPairs]);

  const doSearch = (val: string) => {
    const s = val.trim().toUpperCase();
    const sym = s.endsWith('USDT') ? s : `${s}USDT`;
    setCurrentSymbol(sym);
  };

  const allSymbols = useMemo(() => {
    const others = top10
      .map(c => c.symbol)
      .filter(sym => sym !== currentSymbol)
      .slice(0, 4);
    return [currentSymbol, ...others];
  }, [currentSymbol, top10]);

  const { data: plansBatch } = useSWR<Record<string, InvestmentPlan[]>>(
    allSymbols.length ? `/getInvestmentPlans?symbols=${allSymbols.join(',')}` : null,
    fetcher
  );

  console.log({ usdtPairs, currentSymbol });

  const whitelist = ['BTCUSDT', 'ETHUSDT'];
  const valid = usdtPairs.includes(currentSymbol) || whitelist.includes(currentSymbol);
  // const valid = true;
  const { data: kline } = useSWR<Point[]>(
    valid ? `/getKlines?symbol=${currentSymbol}&interval=1d&startTime=1625097600000&endTime=1625702400000` : null,
    fetcher
  );

  const { data: analysis } = useSWR<AnalysisData>(
    valid ? `/runAnalysis?symbol=${currentSymbol}` : null,
    fetcher
  );

  const { data: forecast } = useSWR<Record<string, Point[]>>(
    valid ? `/getFutureForecast?symbol=${currentSymbol}` : null,
    fetcher
  );

  return (
    <div style={{ padding: 24 }}>
      {/* 搜索栏 */}
      <Card style={{ marginBottom: 24 }}>
        <Search
          placeholder="输入交易对 (如 BTCUSDT)"
          enterButton="分析"
          onSearch={doSearch}
          defaultValue={currentSymbol}
          style={{ width: 300 }}
        />
        <Switch
          checked={showDetails}
          onChange={setShowDetails}
          checkedChildren="显示详情"
          unCheckedChildren="隐藏详情"
          style={{ marginLeft: 16 }}
        />
      </Card>

      {/* 热门横幅 */}
      <Card style={{ marginBottom: 24 }}>
        <HotCoinsBanner plans={top10.slice(0, 5)} onSelect={doSearch} />
      </Card>

      {/* 多期限投资建议 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={4}>💡 多期限投资建议（前 5）</Title>
        <InvestmentPlansList
          plans={allSymbols
            .map(sym => plansBatch?.[sym]?.[0])
            .filter((p): p is InvestmentPlan => Boolean(p))}
          onOrder={p => alert(`下单 ${p.symbol} ${p.amountUSDT} USDT`)}
          expandableDetails={showDetails ? plansBatch : undefined}
        />
      </Card>

      {/* 汇率列表 + 历史图 */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={6}>
            <RateList rates={top10.map(c => c.symbol)} onSelect={doSearch} />
          </Col>
          <Col span={18}>
            {valid ? (
              <Tabs
                activeKey={currentSymbol}
                onChange={doSearch}
                items={[
                  {
                    key: currentSymbol,
                    label: currentSymbol,
                    children: <PriceHistoryChart data={kline || []} />,
                  },
                ]}
              />
            ) : (
              <Alert message="该交易对暂无历史行情数据" type="warning" />
            )}
          </Col>
        </Row>
      </Card>

      {/* AI 分析与建议 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={4}>🤖 AI 分析与建议</Title>
        {valid && analysis?.recommendations.immediate.length ? (
          <TradingRecommendations recommendations={analysis.recommendations} />
        ) : (
          <Alert message="暂无 AI 推荐策略" type="info" />
        )}
      </Card>

      {/* 未来走势预测 */}
      <Card style={{ marginBottom: 24 }}>
        <Title level={4}>🔮 未来走势预测</Title>
        {valid ? (
          <FutureForecast data={forecast?.[currentSymbol] || []} />
        ) : (
          <Alert message="该交易对暂无预测数据" type="warning" />
        )}
      </Card>
    </div>
  );
};

export default Home;
